import { useEffect, useRef, useState } from "react";

/* Answers already given in a quiz survive leaving it (nv-005).

   Browser back mid-quiz steps out to the module's sub-constellation, the
   same place the quiz's own back lands. useDraft is useState whose value is
   kept here while the quiz is away, so opening the quiz again picks up on
   the same question with the same answers. Memory only: a reload starts
   fresh, and nothing is written to the device or the account. clearDraft
   runs when the quiz hands its result on, and clearAllDrafts on sign out. */

const drafts = new Map(); // "quiz:name" -> value

export function useDraft(quiz, name, initial) {
  const k = `${quiz}:${name}`;
  const [value, setValue] = useState(() =>
    drafts.has(k) ? drafts.get(k) : typeof initial === "function" ? initial() : initial
  );
  // The quiz unmounts in the same commit that hands its result on, so no
  // write lands after clearDraft.
  useEffect(() => { drafts.set(k, value); }, [k, value]);
  return [value, setValue];
}

/** The quiz's onComplete: clears the draft and hands the result on, but only
    while the quiz is on screen. A last answer's short pause can end after
    browser back took the quiz away; that late call is dropped, so the
    module doesn't jump back to a result, and the draft keeps the answers. */
export function useHandOn(quiz, handOn) {
  const live = useRef(true);
  const fn = useRef(handOn);
  fn.current = handOn;
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  return (...a) => {
    if (!live.current) return;
    clearDraft(quiz);
    fn.current(...a);
  };
}

export function clearDraft(quiz) {
  const prefix = `${quiz}:`;
  for (const k of [...drafts.keys()]) if (k.startsWith(prefix)) drafts.delete(k);
}

export function clearAllDrafts() {
  drafts.clear();
}
